import { useCallback } from 'react';

import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { ENV } from '@/constants/env';
import { HttpClient, buildEndpointUrl } from '@/libs/axios';
import { MutateReturnOptions } from '@/models/api';
import { getAxiosBackendError } from '@/utils/errors';

import { Authorization } from '../../types/auth';

export const getRefreshTokenMutateKey = () => ['refreshToken'];

export const getRefreshTokenEndpoint = () => buildEndpointUrl('AUTH_TOKEN');

export const apiRefreshToken = (refreshToken: string) => {
    const bodyFormData = new FormData();
    bodyFormData.append('grant_type', 'refresh_token');
    bodyFormData.append('client_id', ENV.API_CLIENT_ID);
    bodyFormData.append('client_secret', ENV.API_SECRET);
    bodyFormData.append('refresh_token', refreshToken);

    const headers = { 'Content-Type': 'application/form-data' };

    return HttpClient.post<Authorization>(getRefreshTokenEndpoint(), bodyFormData, { headers });
};

export const useRefreshToken = () => {
    const { mutateAsync, isPending: isRefreshing } = useMutation<Authorization, AxiosError, string>({
        mutationKey: getRefreshTokenMutateKey(),
        mutationFn: (refreshToken) => apiRefreshToken(refreshToken),
    });

    const refreshToken = useCallback(
        async (refreshToken: string, { onSuccess, onError, onSettled }: MutateReturnOptions<Authorization> = {}) => {
            let response: Authorization;

            try {
                response = await mutateAsync(refreshToken);
            } catch (error: unknown) {
                await onError?.(getAxiosBackendError(error, true));
                await onSettled?.();
                return;
            }

            await onSuccess?.(response);
            await onSettled?.();
        },
        [mutateAsync],
    );

    return { refreshToken, isRefreshing };
};
